import { useMemo, useEffect, useState } from 'react'
import { withTheme } from 'emotion-theming'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { withTranslation, Link } from 'server/i18n'
import { SimpleImg } from 'react-simple-img'
import { getImageURL, getBoatLink } from 'helpers/middleware'

import ArrowAltIcon from 'public/icons/arrow-alt.svg'

import style from './BoatListingItem.style'

const BoatListingItemStyled = styled.div(props => ({ ...style(props) }))

const BoatListingItem = ({
    t,
    i18n,
    id,
    images,
    title,
    type,
    model,
    condition,
    conditionCrm,
    year,
    length,
    // accommodation,
    price,
    currency,
    link,
    brand,
    vat
}) => {
    const [shouldRender, setShouldRender] = useState(!process.browser)

    const trueCondition = useMemo(() => {
        if (conditionCrm) {
            if (conditionCrm === 'Stock') return 'new-stock'
        }
        return condition
    }, [condition, conditionCrm])

    useEffect(() => {
        const timeout = setTimeout(() => {
            setShouldRender(true)
        })
        return () => {
            clearTimeout(timeout)
        }
    }, [shouldRender])

    useEffect(() => {
        setShouldRender(false)
    }, [id])

    const boatImage = useMemo(() => {
        return {
            src: getImageURL(images, { position: 'listing', size: 'featured', hasFallback: true }),
            alt: title
        }
    }, [images, title])

    const boatLink = useMemo(() => {
        return getBoatLink(type, link, i18n.language)
    }, [type, link, i18n.language])

    return (
        <BoatListingItemStyled>
            <div key={id}>
                {shouldRender ? (
                    <>
                        <Link key={boatLink.as} {...boatLink}>
                            <a className="blockLink">More details about {title}</a>
                        </Link>

                        <div className="ItemImage" key={boatImage.src}>
                            {boatImage.src ? (
                                <SimpleImg key={boatImage.src} src={boatImage.src} alt={boatImage.title} />
                            ) : null}
                        </div>
                    </>
                ) : null}
                <div className="ItemContent">
                    <div className="Arrow">
                        <ArrowAltIcon />
                    </div>

                    <div className="title">{title}</div>

                    <ul className="no-bullet specification">
                        <li>
                            <span>
                                {t('controls:condition')}: {t(`controls:${trueCondition}`)}
                            </span>
                        </li>
                        {year && (
                            <li>
                                {t('controls:year')}: <strong>{year}</strong>
                            </li>
                        )}
                        <li>
                            <span>
                                {t('controls:length')}: <strong>{length} m</strong>
                            </span>
                        </li>
                    </ul>
                    <div className="BoatPrice">
                        {+price === 0 ? '' : currency} {+price === 0 ? t('price-upon-request') : price}
                        {''} {trueCondition === 'New' && +price !== 0 ? t('controls:standard-spec-price') : ''}
                        {+price !== 0 && <span className="vat">{vat === 'Yes' ? t('vat-yes') : t('vat-no')}</span>}
                    </div>
                </div>
            </div>
        </BoatListingItemStyled>
    )
}

BoatListingItem.propTypes = {
    t: PropTypes.func.isRequired,
    i18n: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    id: PropTypes.string.isRequired,
    images: PropTypes.oneOfType([() => null, PropTypes.object]),
    type: PropTypes.string,
    model: PropTypes.string,
    title: PropTypes.string.isRequired,
    condition: PropTypes.string.isRequired,
    conditionCrm: PropTypes.string.isRequired,
    year: PropTypes.string,
    length: PropTypes.number,
    price: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    brand: PropTypes.string.isRequired,
    vat: PropTypes.string.isRequired
}

BoatListingItem.defaultProps = {
    images: {},
    year: false,
    length: null,
    type: null,
    model: null
}

export default withTranslation('common', 'controls')(withTheme(BoatListingItem))
